import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import { useParams, Link, useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AudioPlayer from './AudioPlayer';
import SocialPostCard from './SocialPostCard';
import TldrDisplay from './TldrDisplay';
import HeadlinesDisplay from './HeadlinesDisplay';
import ImageCard from './ImageCard';

/**
 * Results Page Component
 * Displays all spawned content for an article
 * Uses dark header + white card pattern
 */
const ResultsPage = () => {
  const { slug } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Determine if this is the tenant app version or free version
  const isTenantApp = location.pathname.startsWith('/app');
  const basePath = isTenantApp ? '/app/article-to-media' : '/free';

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHideModal, setShowHideModal] = useState(false);
  const [hideReason, setHideReason] = useState('');
  const [hiding, setHiding] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [hiddenTitle, setHiddenTitle] = useState('');
  const [adminViewingHidden, setAdminViewingHidden] = useState(false);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  // Check if content appears incomplete (less than 500 chars)
  const isContentIncomplete = article?.article_content && article.article_content.length < 500;

  const fetchArticle = async () => {
    try {
      const response = await fetch(`/api/tools/article/${slug}`);
      const data = await response.json();

      if (data.error) {
        if (data.isHidden) {
          setIsUnderReview(true);
          setHiddenTitle(data.title || '');
        }
        setError(data.error);
        setLoading(false);
        return;
      }

      setArticle(data.article);
      if (data.adminViewingHidden) {
        setAdminViewingHidden(true);
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to load article');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleHideArticle = async () => {
    setHiding(true);
    try {
      const response = await fetch(`/api/tools/article/${slug}/hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: hideReason })
      });
      const data = await response.json();
      if (data.success) {
        setShowHideModal(false);
        history.push(basePath);
      } else {
        setError(data.error || 'Failed to hide article');
      }
    } catch (err) {
      setError('Failed to hide article');
    } finally {
      setHiding(false);
    }
  };

  const handleReportIncomplete = async () => {
    setFeedbackSubmitting(true);
    try {
      const response = await fetch(`/api/tools/article/${slug}/report-incomplete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contentLength: article.article_content?.length || 0,
          sourceUrl: article.source_url
        })
      });
      const data = await response.json();
      if (data.success) {
        setShowFeedbackModal(false);
        setFeedbackSubmitted(true);
      } else {
        setError(data.error || 'Failed to submit feedback');
      }
    } catch (err) {
      setError('Failed to submit feedback');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // Loading state with dark header
  if (loading) {
    return (
      <>
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          padding: '100px 0 3rem'
        }}>
          <Container className="text-center">
            <Spinner animation="border" variant="light" />
            <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: '1rem' }}>Loading your spawned media...</p>
          </Container>
        </div>
      </>
    );
  }

  // Error state - article under review
  if (isUnderReview) {
    return (
      <>
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          padding: '100px 0 3rem'
        }}>
          <Container>
            <h1 style={{ color: 'white', fontWeight: '700', fontSize: '2rem', marginBottom: '0.5rem' }}>
              Article Under Review
            </h1>
            {hiddenTitle && <p style={{ color: 'rgba(255,255,255,0.8)' }}>{hiddenTitle}</p>}
          </Container>
        </div>
        <Container style={{ paddingTop: '2rem', paddingBottom: '5rem' }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            maxWidth: '600px'
          }}>
            <Alert variant="info" className="mb-4">
              This article may be incomplete, so we're looking into it.
            </Alert>
            <a
              href="mailto:sales@spawnry.ai?subject=Interest in Spawnry"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                borderRadius: '10px',
                fontWeight: '600',
                textDecoration: 'none'
              }}
            >
              Contact Sales
            </a>
          </div>
        </Container>
      </>
    );
  }

  // Generic error state
  if (error) {
    return (
      <>
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          padding: '100px 0 3rem'
        }}>
          <Container>
            <h1 style={{ color: 'white', fontWeight: '700', fontSize: '2rem' }}>Error</h1>
          </Container>
        </div>
        <Container style={{ paddingTop: '2rem', paddingBottom: '5rem' }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            maxWidth: '600px'
          }}>
            <Alert variant="danger" className="mb-4">{error}</Alert>
            <Link to={basePath}>
              <Button variant="outline-primary">Back to Tool</Button>
            </Link>
          </div>
        </Container>
      </>
    );
  }

  // Article not found
  if (!article) {
    return (
      <>
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          padding: '100px 0 3rem'
        }}>
          <Container>
            <h1 style={{ color: 'white', fontWeight: '700', fontSize: '2rem' }}>Not Found</h1>
          </Container>
        </div>
        <Container style={{ paddingTop: '2rem', paddingBottom: '5rem' }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            maxWidth: '600px'
          }}>
            <Alert variant="warning" className="mb-4">Article not found</Alert>
            <Link to={basePath}>
              <Button variant="outline-primary">Back to Tool</Button>
            </Link>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article.meta_title || `${article.article_title} | Spawnry`}</title>
        <meta name="description" content={article.meta_description || article.tldr_content?.[0]} />
        <meta property="og:title" content={article.article_title} />
        <meta property="og:description" content={article.meta_description || article.tldr_content?.[0]} />
        {article.og_image_url && <meta property="og:image" content={article.og_image_url} />}
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`https://spawnry.ai/free/${slug}`} />
      </Helmet>

      {/* Dark Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '100px 0 3rem',
        marginTop: '-2rem',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        width: '100vw'
      }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              {/* Admin notice */}
              {adminViewingHidden && (
                <Alert variant="warning" className="mb-3" style={{ background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', color: 'white' }}>
                  <strong>Admin View:</strong> This article is hidden and under review.
                  {article.hidden_reason && <span> Reason: "{article.hidden_reason}"</span>}
                </Alert>
              )}

              <div style={{
                width: 'clamp(40px, 8vw, 64px)',
                height: 'clamp(40px, 8vw, 64px)',
                margin: '0 auto 1.5rem',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h1 style={{ color: 'white', fontWeight: '700', fontSize: 'clamp(1.75rem, 3vw, 2.25rem)', marginBottom: '0.75rem' }}>
                Spawned Media
              </h1>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.1rem', marginBottom: '2rem' }}>
                Your article has been transformed into multiple formats
              </p>

              {/* 3-Step Indicator */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 'clamp(0.25rem, 2vw, 1rem)', flexWrap: 'wrap' }}>
                {['Input Article', 'Fetch Content', 'Spawn Media'].map((stepName, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      minWidth: '24px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff'
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 'clamp(0.7rem, 2vw, 0.85rem)', fontWeight: '500', whiteSpace: 'nowrap' }}>
                      {stepName}
                    </span>
                    {index < 2 && (
                      <div style={{
                        width: '16px',
                        height: '2px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        marginLeft: '0.25rem'
                      }} />
                    )}
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* White Content Area */}
      <Container style={{ marginTop: '-1.5rem', paddingBottom: '5rem' }}>

        {/* Article Metadata Card */}
        <Row className="justify-content-center mb-4">
          <Col lg={10}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '1.5rem',
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
            }}>
              <h2 style={{ fontWeight: '700', color: '#1a1a2e', fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)', marginBottom: '0.75rem', lineHeight: '1.3' }}>
                {article.article_title}
              </h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', color: '#64748b', fontSize: '0.9rem', marginBottom: '1rem' }}>
                {article.article_outlet && <span>{article.article_outlet}</span>}
                {article.article_byline && <span>• {article.article_byline}</span>}
                <span>• Spawned {formatDate(article.completed_at)}</span>
              </div>
              <a
                href={article.source_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: '#f1f5f9',
                  color: '#475569',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  textDecoration: 'none',
                  fontWeight: '500',
                  transition: 'background 0.2s'
                }}
              >
                View Original Article
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </a>
            </div>
          </Col>
        </Row>

        {/* TLDR & Audio Section - Both cards in a row with equal widths */}
        <Row className="justify-content-center mb-4">
          <Col lg={10}>
            <Row>
              {article.tldr_content && (
                <Col md={article.audio ? 6 : 12} className="mb-4 mb-md-0">
                  <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                    height: '100%'
                  }}>
                    <h5 style={{ fontWeight: '600', color: '#1a1a2e', marginBottom: '1rem' }}>TLDR Summary</h5>
                    <TldrDisplay bullets={article.tldr_content} />
                  </div>
                </Col>
              )}

              {article.audio && (
                <Col md={article.tldr_content ? 6 : 12}>
                  <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                    height: '100%'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h5 style={{ fontWeight: '600', color: '#1a1a2e', margin: 0 }}>
                        {article.audio?.audio_mode === 'reading_30s' ? '30-Second Audio Preview' : 'Audio Summary'}
                      </h5>
                  {(() => {
                    const isDryRun = article.audio.s3_url?.includes('example.com/mock');
                    const handleDownloadAudio = () => {
                      if (isDryRun) return;
                      const filename = article.audio?.audio_mode === 'reading_30s'
                        ? `${article.slug}-audio-preview.mp3`
                        : `${article.slug}-audio-summary.mp3`;
                      const downloadUrl = `/api/tools/article/download?url=${encodeURIComponent(article.audio.s3_url)}&filename=${encodeURIComponent(filename)}`;
                      window.location.href = downloadUrl;
                    };
                    return (
                      <button
                        onClick={handleDownloadAudio}
                        disabled={isDryRun}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '36px',
                          height: '36px',
                          backgroundColor: '#f1f5f9',
                          color: '#64748b',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: isDryRun ? 'not-allowed' : 'pointer',
                          opacity: isDryRun ? 0.5 : 1,
                          transition: 'all 0.2s ease'
                        }}
                        title={isDryRun ? 'Audio not available in test mode' : 'Download audio'}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                      </button>
                    );
                  })()}
                </div>
                <AudioPlayer src={article.audio.s3_url} slug={article.slug} />
              </div>
            </Col>
          )}
            </Row>
          </Col>
        </Row>

        {/* Alternative Headlines Section */}
        {article.headlines && article.headlines.length > 0 && (
          <Row className="justify-content-center mb-4">
            <Col lg={10}>
              <HeadlinesDisplay headlines={article.headlines} />
            </Col>
          </Row>
        )}

        {/* Social Posts Section */}
        {article.socialPosts && article.socialPosts.length > 0 && (
          <Row className="justify-content-center mb-4">
            <Col lg={10}>
              <h4 style={{ fontWeight: '600', color: '#1a1a2e', marginBottom: '1rem' }}>Spawned Social Posts</h4>
              <Row>
                {article.socialPosts.map((post, index) => (
                  <Col lg={4} md={6} key={post.id} className="mb-4">
                    <SocialPostCard
                      post={post}
                      articleSlug={article.slug}
                      postNumber={index + 1}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        )}

        {/* Generated Images Section */}
        {article.images && article.images.length > 0 && (
          <Row className="justify-content-center mb-4">
            <Col lg={10}>
              <h4 style={{ fontWeight: '600', color: '#1a1a2e', marginBottom: '1rem' }}>Generated Images</h4>
              <Row>
                {article.images.map((image, index) => (
                  <Col lg={4} md={6} key={image.id} className="mb-4">
                    <ImageCard
                      image={image}
                      articleSlug={article.slug}
                      imageNumber={index + 1}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        )}

        {/* Article Content Section */}
        <Row className="justify-content-center">
          <Col lg={10}>
            {article.article_content && (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                marginBottom: '2rem',
                overflow: 'hidden'
              }}>
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9' }}>
                  <h5 style={{ fontWeight: '600', color: '#1a1a2e', margin: 0 }}>Extracted Article Content</h5>
                  <small style={{ color: '#94a3b8' }}>
                    {article.article_content?.length || 0} characters extracted
                  </small>
                </div>
                <div style={{ padding: '1.5rem' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: '#64748b' }}>Title:</strong> {article.article_title}
                  </div>
                  {article.article_byline && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong style={{ color: '#64748b' }}>Byline:</strong> {article.article_byline}
                    </div>
                  )}
                  {article.published_date && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong style={{ color: '#64748b' }}>Published:</strong> {formatDate(article.published_date)}
                    </div>
                  )}
                  <hr style={{ borderColor: '#f1f5f9', margin: '1.5rem 0' }} />
                  <div style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.9rem',
                    lineHeight: '1.7',
                    color: '#374151'
                  }}>
                    {article.article_content}
                  </div>
                </div>
              </div>
            )}

            {/* Incomplete Content Warning */}
            {isContentIncomplete && !feedbackSubmitted && (
              <Alert variant="warning" style={{
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.2)',
                borderRadius: '12px',
                marginBottom: '2rem'
              }}>
                <Alert.Heading style={{ fontSize: '1rem', color: '#b45309' }}>
                  Content May Be Incomplete
                </Alert.Heading>
                <p className="mb-2" style={{ color: '#92400e' }}>
                  The extracted content appears shorter than expected ({article.article_content?.length || 0} characters).
                  Some websites require additional loading steps to show full content.
                </p>
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => setShowFeedbackModal(true)}
                >
                  Report Incomplete Content
                </Button>
              </Alert>
            )}

            {feedbackSubmitted && (
              <Alert variant="success" style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '12px',
                marginBottom: '2rem'
              }}>
                Thank you for your feedback! We'll improve our extraction for this website.
              </Alert>
            )}
          </Col>
        </Row>

      </Container>

      {/* Hide Article Modal */}
      <Modal show={showHideModal} onHide={() => setShowHideModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Article</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This will hide the article from public view while we review it. In general, only bad outputs will be removed, but please give any useful context to help with this decision.</p>
          <Form.Group>
            <Form.Label>Reason (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Why should this article be deleted?"
              value={hideReason}
              onChange={(e) => setHideReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHideModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleHideArticle} disabled={hiding}>
            {hiding ? 'Hiding...' : 'Delete Article'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Incomplete Content Feedback Modal */}
      <Modal show={showFeedbackModal} onHide={() => setShowFeedbackModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Report Incomplete Content</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            It looks like we may not have extracted the full article content. This often happens when
            websites use "Read More" buttons that load content dynamically.
          </p>
          <p>
            By reporting this, you'll help us improve our extraction system for <strong>{article?.source_domain}</strong>.
          </p>
          <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px' }}>
            <small style={{ color: '#64748b' }}>
              <strong>Current content:</strong> {article?.article_content?.length || 0} characters<br />
              <strong>Source:</strong> {article?.source_url}
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFeedbackModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleReportIncomplete} disabled={feedbackSubmitting}>
            {feedbackSubmitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ResultsPage;
